import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  Heart, 
  Star, 
  User, 
  MessageCircle, 
  Users, 
  Download,
  Sun,
  Moon,
  Menu,
  X,
  Settings,
  LogOut,
  ChevronDown,
  Sparkles,
  Plus,
  History,
  CreditCard,
  Phone,
  FileText
} from 'lucide-react';
import { getBackendUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { performLogout } from '../utils/logout';
import { useKarmaBalance } from '../hooks/useKarmaBalance';
import KarmaWheel from './KarmaWheel';
import Icon from './ui/Icon';
import AppShellFallback from './AppShellFallback';
const SubscriptionManagement = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './SubscriptionManagement'));
const ToolsMoreModal = React.lazy(() => import(/* webpackChunkName: "panchang" */ './dashboard-v2/ToolsMoreModal'));

const MobileLayout = ({ children, activeTab, onTabChange, userInfo, onActionClick, onKarmaClick, onRewardClaimed }) => {
  const auth = useAuth();
  const [theme, setTheme] = useState('cosmic');
  const [showMenu, setShowMenu] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [toolsMoreOpen, setToolsMoreOpen] = useState(false);
  const [toolsMoreTab, setToolsMoreTab] = useState('root');
  const menuRef = useRef(null);

  // Header karma pill — the ONE surface on screen at the moment a payment
  // completes, so it's the first to move onto the shared hook (T1-2a). Holds
  // onto the last known-good balance across a transient error/expired-session
  // refresh instead of collapsing to 0 — a failed read is not a real balance.
  const walletStatus = useKarmaBalance(userInfo?.user_id);
  const [karmaBalance, setKarmaBalance] = useState(0);
  useEffect(() => {
    if (walletStatus.status === 'known') setKarmaBalance(walletStatus.balance);
  }, [walletStatus.status, walletStatus.balance]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMenu]);

  // Theme configurations
  const themes = {
    cosmic: {
      name: 'Cosmic',
      primary: 'from-purple-900 via-blue-900 to-indigo-900',
      secondary: 'from-purple-600 to-indigo-600',
      accent: 'var(--gold-primary)',
      card: 'bg-white/10 backdrop-blur-lg border-white/20',
      text: 'text-white',
      subtext: 'text-gray-300'
    },
    minimal: {
      name: 'Minimal',
      primary: 'from-gray-50 to-white',
      secondary: 'from-blue-500 to-purple-500',
      accent: '#6366f1',
      card: 'bg-white shadow-xl border border-gray-100',
      text: 'text-gray-900',
      subtext: 'text-gray-600'
    },
    premium: {
      name: 'Premium',
      primary: 'from-amber-50 via-orange-50 to-yellow-50',
      secondary: 'from-amber-500 to-orange-500',
      accent: '#d97706',
      card: 'bg-white/90 backdrop-blur-sm shadow-2xl border border-amber-200',
      text: 'text-amber-900',
      subtext: 'text-amber-700'
    }
  };

  const currentTheme = themes[theme];

  // Bottom navigation items - Chitragupt is hero product
  const navItems = [
    { id: 'dashboard', iconName: 'home', label: 'Home' },
    { id: 'match', iconName: 'heart', label: 'Match' },
    { id: 'chat', iconName: 'chat', label: 'ChitraGupt' },
    { id: 'reports', iconName: 'scroll', label: 'Reports' }
  ];

  // Display nav items - same for all views now since ChitraGupt is always visible
  const displayNavItems = navItems;

  // Quick actions for sidebar menu (keeping only non-redundant actions)
  const quickActions = [
    { 
      id: 'download', 
      icon: Download, 
      label: 'Download Reports',
      action: () => onActionClick('download')
    }
  ];

  // Handle logout — see ModernProfile: auth.logout() owns the sequence. This
  // path previously cleared jyotish_session_token but not jyotish_auth_user,
  // which AuthContext seeds state from synchronously (JP-536).
  const handleLogout = async () => {
    if (auth?.logout) {
      await auth.logout();
    } else {
      await performLogout({ apiUrl: getBackendUrl() });
    }
    window.location.href = '/';
  };

  const toggleTheme = () => {
    const themeKeys = Object.keys(themes);
    const currentIndex = themeKeys.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    setTheme(themeKeys[nextIndex]);
  };

  return (
    <div className={`mobile-layout min-h-screen w-full bg-gradient-to-br ${currentTheme.primary}`}>
      {/* Top Header - Responsive: Compact on mobile, expanded on desktop */}
      <div className="top-header sticky top-0 z-50 bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between px-4 lg:px-8 py-3 lg:py-4">
            {/* Logo and App Name */}
            <div className="flex items-center space-x-3">
              <motion.div
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg overflow-hidden flex items-center justify-center"
              >
                <img 
                  src="/logo-mobile.png" 
                  alt="JyotishPuraan" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <div>
                <h1 className={`text-lg lg:text-xl font-bold ${currentTheme.text}`}>
                  JyotishPuraan
                </h1>
                <p className={`text-xs lg:text-sm ${currentTheme.subtext}`}>
                  {userInfo?.name || 'Welcome back'}
                </p>
              </div>
            </div>

            {/* Desktop: Show more nav items */}
            <div className="hidden lg:flex items-center space-x-6">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                    activeTab === item.id 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon name={item.iconName} size={16} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Karma Pill + Avatar with Dropdown Menu */}
            <div className="flex items-center space-x-2 lg:space-x-4">
              {/* Karma Pill — taps open the Karma Store (top-up). The trailing
                  "+" makes the top-up affordance explicit so the balance no
                  longer reads as a dead label. */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onKarmaClick}
                title="Top up Karma"
                aria-label={`Karma balance ${karmaBalance.toLocaleString()} — tap to top up`}
                className={`px-2 lg:px-4 py-1 lg:py-2 rounded-full backdrop-blur-sm flex items-center gap-1 lg:gap-2 text-xs lg:text-sm font-medium transition-all cursor-pointer ${
                  karmaBalance > 0
                    ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-300'
                    : 'bg-white/10 border border-white/20 text-white/70'
                }`}
                data-testid="header-karma-pill"
              >
                <Sparkles className="w-3 h-3 lg:w-4 lg:h-4" />
                <span>{karmaBalance.toLocaleString()}</span>
                <span className="hidden lg:inline text-white/50">Karma</span>
                <span className="flex items-center justify-center w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-amber-400/90 text-[#1a1233]" title="Top up">
                  <Plus className="w-2.5 h-2.5 lg:w-3 lg:h-3" strokeWidth={3} />
                </span>
              </motion.button>

              {/* Karma Wheel — daily spin chip. Same shared header at every
                  width, which is what fixes the desktop-missing bug (the old
                  DiyaPrasadCard was only mounted in PremiumDashboard's
                  md:hidden mobile-only block). */}
              <KarmaWheel userInfo={userInfo} onRewardClaimed={walletStatus.refresh} />

              {/* Avatar Dropdown - Larger on desktop */}
              <div className="relative" ref={menuRef}>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center space-x-1 lg:space-x-2 p-1 lg:p-1.5 pr-2 lg:pr-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-colors"
                >
                  <div className="w-7 h-7 lg:w-9 lg:h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <span className="text-white font-semibold text-xs lg:text-sm">
                      {userInfo?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <ChevronDown className={`w-3 h-3 text-white/70 transition-transform ${showMenu ? 'rotate-180' : ''}`} />
                </motion.button>

                {/* Dropdown Menu */}
                <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 w-56 bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden z-[60]"
                >
                  {/* User Info Section */}
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="text-white font-medium">{userInfo?.name || 'User'}</p>
                    <p className="text-gray-400 text-xs">Astrology Enthusiast</p>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    {/* 1. My Subscription */}
                    <button
                      onClick={() => {
                        setShowSubscription(true);
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="dropdown-subscription"
                    >
                      <CreditCard className="w-4 h-4 text-purple-400" />
                      <span className="text-white text-sm">My Subscription</span>
                    </button>

                    {/* 2. Past Readings / Saved Sessions */}
                    <button
                      onClick={() => {
                        onTabChange('saved-sessions');
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="dropdown-past-readings"
                    >
                      <History className="w-4 h-4 text-amber-400" />
                      <span className="text-white text-sm">Past Readings</span>
                    </button>

                    {/* 3. Profile Settings */}
                    <button
                      onClick={() => {
                        onTabChange('profile');
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="dropdown-profile-settings"
                    >
                      <Settings className="w-4 h-4 text-blue-400" />
                      <span className="text-white text-sm">Profile Settings</span>
                    </button>

                    {/* 4. Download Reports */}
                    <button
                      onClick={() => {
                        onTabChange('reports');
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="dropdown-download-reports"
                    >
                      <Download className="w-4 h-4 text-emerald-400" />
                      <span className="text-white text-sm">Download Reports</span>
                    </button>

                    {/* 5. FAQ */}
                    <button
                      onClick={() => {
                        setToolsMoreTab('faqs');
                        setToolsMoreOpen(true);
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="dropdown-faq"
                    >
                      <FileText className="w-4 h-4 text-yellow-400" />
                      <span className="text-white text-sm">FAQ</span>
                    </button>

                    {/* 6. Support */}
                    <a
                      href="https://wa.me/918602007775?text=Hi%2C%20I%20need%20help%20with%20JyotishPuraan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors text-white block"
                      onClick={() => setShowMenu(false)}
                      data-testid="dropdown-support"
                    >
                      <Phone className="w-4 h-4 text-cyan-400" />
                      <span className="text-white text-sm inline-block ml-3">Support</span>
                    </a>

                    {/* 7. Privacy/Terms/Refund */}
                    <button
                      onClick={() => {
                        window.open('/privacy', '_blank');
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="dropdown-legal"
                    >
                      <FileText className="w-4 h-4 text-orange-400" />
                      <span className="text-white text-sm">Privacy / Terms / Refund</span>
                    </button>

                    {/* Theme Toggle */}
                    <button
                      onClick={() => {
                        toggleTheme();
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors"
                      data-testid="dropdown-theme-toggle"
                    >
                      {theme === 'cosmic' ? (
                        <Sun className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <Moon className="w-4 h-4 text-purple-400" />
                      )}
                      <span className="text-white text-sm">Theme: {currentTheme.name}</span>
                    </button>

                    {/* 8. Logout */}
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-white/10 transition-colors border-t border-white/10 mt-1"
                      data-testid="dropdown-logout"
                    >
                      <LogOut className="w-4 h-4 text-rose-500" />
                      <span className="text-white text-sm">Logout</span>
                    </button>
                  </div>
                </motion.div>
              )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - Max width on desktop */}
      <div className="main-content pb-20 lg:pb-8 px-0">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </div>

      {/* Floating Action Buttons - REMOVED: Redundant with bottom navigation */}

      {/* Bottom Navigation - Mobile only */}
      <div className="bottom-navigation fixed bottom-0 left-0 right-0 z-50 lg:hidden">
        <div className="bg-gray-900/95 backdrop-blur-md border-t border-gray-700/50">
          <div className="flex items-center justify-around px-2 py-2">
            {displayNavItems.map((item, index) => {
              const isActive = activeTab === item.id;
              return (
                <motion.button
                  key={item.id}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onTabChange(item.id)}
                  className={`flex flex-col items-center space-y-1 p-3 rounded-xl min-w-0 flex-1 relative ${
                    isActive ? 'bg-amber-500/20' : 'bg-transparent'
                  }`}
                  style={{
                    background: isActive ? 'rgba(231,185,90,0.12)' : 'transparent',
                  }}
                  data-testid={`nav-tab-${item.id}`}
                >
                  {/* Active indicator */}
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 rounded-xl"
                      style={{ background: 'rgba(231,185,90,0.10)' }}
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  
                  <motion.div
                    animate={{
                      scale: isActive ? 1.1 : 1
                    }}
                    transition={{ duration: 0.2 }}
                    className="relative z-10"
                    style={{ color: isActive ? 'var(--gold)' : 'var(--dim)' }}
                  >
                    <Icon name={item.iconName} size={20} />
                  </motion.div>
                  
                  <span
                    className="relative z-10 font-semibold text-[10px]"
                    style={{ color: isActive ? 'var(--gold)' : 'var(--dim)' }}
                  >
                    {item.label}
                  </span>
                </motion.button>
              );
            })}
          </div>

          {/* iPhone-style home indicator */}
          <div className="flex justify-center pb-2">
            <div className="w-32 h-1 bg-gray-600 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Overlay Modals */}
      <React.Suspense fallback={<AppShellFallback />}>
        {toolsMoreOpen && (
          <ToolsMoreModal
            isOpen={toolsMoreOpen}
            onClose={() => setToolsMoreOpen(false)}
            userInfo={userInfo}
            onNavigate={onTabChange}
            initialView={toolsMoreTab}
          />
        )}

        {showSubscription && (
          <SubscriptionManagement
            userId={userInfo?.user_id}
            onClose={() => setShowSubscription(false)}
            onNavigate={(view) => { if (view === 'reports') onActionClick?.('reports'); }}
          />
        )}
      </React.Suspense>
    </div>
  );
};

export default MobileLayout;
